// Shared Arztbrief document used by all OCR prototypes.
// Real text (not raster bars), grouped into semantic regions so the
// region-detection prototype has something meaningful to draw.

export type Region =
  | 'header'
  | 'patient'
  | 'admin'
  | 'diagnosis'
  | 'befund'
  | 'labor'
  | 'medikation'
  | 'empfehlung'
  | 'signatur';

export type Emphasis = 'title' | 'subtitle' | 'body' | 'small';

export type DocLine = {
  text: string;
  region: Region;
  emphasis?: Emphasis;
};

export const DOC: DocLine[] = [
  { text: 'CHARITÉ — UNIVERSITÄTSMEDIZIN BERLIN', region: 'header', emphasis: 'title' },
  { text: 'Campus Mitte · Innere Medizin · Station 24b', region: 'header', emphasis: 'subtitle' },
  { text: 'Patient: Meier, Johanna · geb. 03.11.1958', region: 'patient' },
  { text: 'Versichertennummer: AOK A123456789 (GKV)', region: 'patient' },
  { text: 'Aufnahme: 14.05.2026   Entlassung: 21.05.2026', region: 'admin' },
  { text: 'Hauptdiagnose: I10.90  Essentielle Hypertonie', region: 'diagnosis' },
  { text: 'Nebendiagnose: E11.9  Diabetes mellitus Typ 2', region: 'diagnosis' },
  { text: 'Befund: Blutdruck 158/94 mmHg in Ruhe', region: 'befund' },
  { text: 'Labor: HbA1c 7,4 %   LDL 142 mg/dl', region: 'labor' },
  { text: 'Medikation: Ramipril 5 mg morgens', region: 'medikation' },
  { text: 'Metformin 850 mg zweimal täglich', region: 'medikation' },
  { text: 'Empfehlung: Wiedervorstellung in 3 Monaten', region: 'empfehlung' },
  { text: 'Unterzeichnet: Dr. med. Weber, Oberarzt', region: 'signatur' },
  { text: 'Ärztliche Schweigepflicht §203 StGB', region: 'signatur', emphasis: 'small' },
];

// Per-region color so detection boxes feel categorical, not uniform.
export const REGION_COLORS: Record<Region, string> = {
  header: '#9DA1A8',
  patient: '#FF7A45',
  admin: '#6FA8FF',
  diagnosis: '#FF7A45',
  befund: '#A78BFA',
  labor: '#34D399',
  medikation: '#FACC15',
  empfehlung: '#9DA1A8',
  signatur: '#9DA1A8',
};

export const REGION_LABELS: Record<Region, string> = {
  header: 'HEADER',
  patient: 'PATIENT',
  admin: 'AUFENTHALT',
  diagnosis: 'DIAGNOSE',
  befund: 'BEFUND',
  labor: 'LABOR',
  medikation: 'MEDIKATION',
  empfehlung: 'EMPFEHLUNG',
  signatur: 'SIGNATUR',
};

// Structured extraction target (Prototype B serializes this to JSON).
export const STRUCTURED = {
  dokument: 'arztbrief',
  klinik: {
    name: 'Charité — Universitätsmedizin Berlin',
    standort: 'Campus Mitte',
    abteilung: 'Innere Medizin · Station 24b',
  },
  patient: {
    name: 'Meier, Johanna',
    geburtsdatum: '1958-11-03',
    versichertennummer: 'A123456789',
    kasse: 'AOK',
    kassenart: 'GKV',
  },
  aufenthalt: {
    aufnahme: '2026-05-14',
    entlassung: '2026-05-21',
  },
  diagnosen: [
    { typ: 'haupt', icd: 'I10.90', text: 'Essentielle Hypertonie' },
    { typ: 'neben', icd: 'E11.9', text: 'Diabetes mellitus Typ 2' },
  ],
  befund: {
    blutdruck_mmhg: '158/94',
  },
  labor: {
    hba1c_prozent: 7.4,
    ldl_mg_dl: 142,
  },
  medikation: [
    { wirkstoff: 'Ramipril', dosis_mg: 5, frequenz: 'morgens' },
    { wirkstoff: 'Metformin', dosis_mg: 850, frequenz: '2x tgl' },
  ],
  empfehlung: 'Wiedervorstellung in 3 Monaten',
  signatur: { name: 'Dr. med. Weber', rolle: 'Oberarzt' },
};

// Prototype C: tokens arrive in *semantic* order, not reading order.
// Each chunk "attends" to a source region (flash a box there as it types).
export type TokenChunk = { region: Region; text: string };

export const TOKEN_STREAM: TokenChunk[] = [
  { region: 'header', text: '{\n  "dokument": "arztbrief",\n' },
  { region: 'header', text: '  "klinik": "Charité — Campus Mitte",\n' },
  { region: 'patient', text: '  "patient": { "name": "Meier, Johanna", "geb": "1958-11-03" },\n' },
  { region: 'patient', text: '  "versicherung": { "kasse": "AOK", "nr": "A123456789" },\n' },
  { region: 'admin', text: '  "aufenthalt": { "von": "2026-05-14", "bis": "2026-05-21" },\n' },
  { region: 'diagnosis', text: '  "icd": ["I10.90", "E11.9"],\n' },
  { region: 'befund', text: '  "blutdruck": "158/94 mmHg",\n' },
  { region: 'labor', text: '  "hba1c": 7.4, "ldl": 142,\n' },
  { region: 'medikation', text: '  "rx": ["Ramipril 5mg", "Metformin 850mg×2"],\n' },
  { region: 'empfehlung', text: '  "followup_m": 3,\n' },
  { region: 'signatur', text: '  "signed_by": "Dr. med. Weber"\n}' },
];

// Helper for prototypes: which lines belong to each region (in order).
export const REGION_LINES: Region[] = (() => {
  const seen: Region[] = [];
  for (const l of DOC) if (!seen.includes(l.region)) seen.push(l.region);
  return seen;
})();
